import React, { useEffect, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'

// Literal colours on purpose: canvas fills cannot read the CSS-variable THEME
// tokens, and these brand hues read well on both the light and dark surfaces.
const COLORS = ['#1c873b', '#30a74f', '#84bf8e', '#f9aa33', '#ffa558', '#0b62d1', '#e88c00']

const GRAVITY = 1600 // px/s²
const DRAG = 1.8 // velocity decay per second (higher = shorter throw)

const styles = createUseStyles({
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 1
  }
})

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  w: number
  h: number
  color: string
  rot: number
  vrot: number
  round: boolean
  wobble: number
  wobbleSpeed: number
}

interface IConfetti {
  // Number of pieces in the burst
  count?: number
  // Total life of the burst, in ms; pieces fade over the last third
  duration?: number
  // Launch point as a fraction of the parent's width / height
  origin?: { x: number; y: number }
  className?: string
}

const rand = (min: number, max: number) => min + Math.random() * (max - min)

const makeParticle = (x: number, y: number): Particle => {
  // Upward cone, ±75° around straight up
  const angle = -Math.PI / 2 + rand(-1.3, 1.3)
  const speed = rand(420, 980)
  const w = rand(7, 12)
  return {
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    w,
    h: w * rand(0.45, 0.7),
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rot: rand(0, Math.PI * 2),
    vrot: rand(-8, 8),
    round: Math.random() < 0.25,
    wobble: rand(0, Math.PI * 2),
    wobbleSpeed: rand(6, 12)
  }
}

/**
 * A one-shot confetti burst drawn on a canvas that covers its (positioned)
 * parent. Fires on mount, cleans up on unmount, and does nothing when the
 * visitor has asked for reduced motion. Sits above siblings but never takes
 * pointer events, so buttons underneath stay clickable.
 */
export const Confetti: React.FC<IConfetti> = ({
  count = 160,
  duration = 3200,
  origin = { x: 0.5, y: 0.3 },
  className
}) => {
  const classes = styles()
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) {
      return undefined
    }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return undefined
    }

    const { width, height } = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.round(width * dpr)
    canvas.height = Math.round(height * dpr)
    ctx.scale(dpr, dpr)

    const particles: Particle[] = []
    for (let i = 0; i < count; i += 1) {
      particles.push(makeParticle(width * origin.x, height * origin.y))
    }

    const start = performance.now()
    let last = start
    let frame = 0

    const tick = (now: number) => {
      const elapsed = now - start
      const dt = Math.min((now - last) / 1000, 0.05)
      last = now
      ctx.clearRect(0, 0, width, height)

      // Full strength for the first two thirds, then fade out
      const fadeStart = duration * 0.66
      const alpha =
        elapsed < fadeStart ? 1 : Math.max(0, 1 - (elapsed - fadeStart) / (duration - fadeStart))
      ctx.globalAlpha = alpha

      let alive = false
      const decay = Math.exp(-DRAG * dt)
      particles.forEach((p) => {
        p.vx *= decay
        p.vy = p.vy * decay + GRAVITY * dt
        p.wobble += p.wobbleSpeed * dt
        p.x += (p.vx + Math.sin(p.wobble) * 40) * dt
        p.y += p.vy * dt
        p.rot += p.vrot * dt
        if (p.y - p.h < height) {
          alive = true
        }

        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rot)
        // Flutter: squash on one axis so the pieces look like they tumble
        ctx.scale(1, Math.abs(Math.cos(p.wobble)) * 0.8 + 0.2)
        ctx.fillStyle = p.color
        if (p.round) {
          ctx.beginPath()
          ctx.arc(0, 0, p.h / 2, 0, Math.PI * 2)
          ctx.fill()
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
        }
        ctx.restore()
      })

      if (elapsed < duration && alive) {
        frame = requestAnimationFrame(tick)
      } else {
        ctx.clearRect(0, 0, width, height)
      }
    }
    frame = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(frame)
    // Fire once per mount; prop changes should not re-burst.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <canvas ref={ref} className={cx(classes.canvas, className)} aria-hidden="true" />
}

export default Confetti
